import { createRef, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import i18nContext from 'components/i18n-context';
import ComplexTranslate from 'components/ComplexTranslate';
import Input from 'uikit/Input/Input';
import Button from 'uikit/Button/Button';
import { deleteNonLatin, emailRegExp, replaceSpaces } from 'services/utils';
import CustomCaptcha from '../../../uikit/CustomCaptcha';

const BusinessRegistration = ({ registrationStore }) => {
  const i18n = useContext(i18nContext);
  const captchaProvider = process.env.REACT_APP_CAPTCHA_PROVIDER;
  const captchaSiteKey =
    process.env.REACT_APP_CAPTCHA_PROVIDER === 'GOOGLE'
      ? process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY?.trim()
      : process.env.REACT_APP_CAPTCHA_PROVIDER === 'TURNSTILE'
        ? process.env.REACT_APP_TURNSTILE_CAPTCHA_SITE_KEY?.trim()
        : null;
  const recaptchaRef = createRef();

  const handleResetCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const form = useFormik({
    validateOnChange: false,
    initialValues: {
      companyName: registrationStore.businessFields.companyName,
      email: registrationStore.generalFields.email,
      captcha: registrationStore.generalFields.captcha
    },
    validationSchema: Yup.object({
      companyName: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('register.error.emptyCompany'))
        .max(250, i18n.getMessage('error.Size')),
      email: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('register.error.emptyEmail'))
        .max(50, i18n.getMessage('error.emailSize'))
        .trim()
        .matches(emailRegExp, { message: i18n.getMessage('register.error.invalidEmail') })
        .email(i18n.getMessage('register.error.invalidEmail')),
      captcha: captchaSiteKey
        ? Yup.string().required(i18n.getMessage('register.error.emptyCaptcha'))
        : Yup.string().nullable(true).notRequired()
    }),
    onSubmit: async (values) => {
      registrationStore.setCompanyName(values.companyName.trim());
      registrationStore.setEmail(replaceSpaces(values.email));
      registrationStore.setCaptcha(values.captcha);
      await registrationStore.registerBusinessAccount();
    }
  });

  const { values, errors, handleSubmit, handleChange, validateField, submitCount, setFieldValue } = form;

  const serverFieldErrors = registrationStore?.error?.fields || {};

  useEffect(() => {
    if (registrationStore.error || errors?.companyName || errors?.email) {
      handleResetCaptcha();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationStore.error, errors?.companyName, errors?.email]);

  return (
    <>
      <form action='#' id='send' onSubmit={handleSubmit} className='form'>
        <Input
          type={'email_phone'}
          label={i18n.getMessage('register.label.company')}
          placeholder={i18n.getMessage('register.company.placeholder')}
          name={'companyName'}
          value={deleteNonLatin(values.companyName)}
          onChange={handleChange}
          onBlur={() => validateField('companyName')}
          initialStatus={submitCount}
          error={
            errors?.companyName ||
            (serverFieldErrors?.companyName && i18n.getMessage(`error.${serverFieldErrors.companyName}`))
          }
        />
        <Input
          type={'email_phone'}
          label={i18n.getMessage('register.label.email')}
          placeholder={i18n.getMessage('register.email.placeholder')}
          name={'email'}
          value={replaceSpaces(values.email)}
          onChange={handleChange}
          onBlur={() => validateField('email')}
          initialStatus={submitCount}
          error={errors?.email || (serverFieldErrors?.email && i18n.getMessage(`error.${serverFieldErrors.email}`))}
        />

        {captchaProvider && captchaSiteKey && (
          <CustomCaptcha
            captchaProvider={captchaProvider}
            siteKey={captchaSiteKey}
            error={errors?.captcha}
            onChange={(token) => setFieldValue('captcha', token)}
            recaptchaRef={recaptchaRef}
          />
        )}

        <Button type={'primary'} roleType={'submit'} size={'large'} fullWidth={true} onClick={() => {}}>
          {i18n.getMessage('login.link.create')}
        </Button>
      </form>
      <div className='register-createAccount-wrapper'>
        <ComplexTranslate
          text={'register.text.creatingAccount'}
          interpolation={{
            user: (
              <a
                href={process.env.REACT_APP_LINK_TERMS_AND_CONDITIONS}
                target='_blank'
                className='link-normal'
                rel='noreferrer'
              >
                {i18n.getMessage('register.text.userAgreement')}
              </a>
            ),
            privacy: (
              <a
                href={process.env.REACT_APP_LINK_PRIVACY_POLICY}
                target='_blank'
                className='link-normal'
                rel='noreferrer'
              >
                {i18n.getMessage('register.text.userPrivacy')}
              </a>
            )
          }}
        />
      </div>
    </>
  );
};

BusinessRegistration.propTypes = {
  registrationStore: MobXPropTypes.observableObject,
  error: PropTypes.any
};

export default inject('registrationStore')(observer(BusinessRegistration));
